import { useTasks } from '../../../contexts/tasks/TasksContext';
import { useTaskFiltering } from './useTaskFiltering';
import { Task } from '../../../types';
import { notify } from '../../../utils/toast';

/**
 * Hook Global de Gestão de Tarefas (useTaskManagement)
 */
export const useTaskManagement = (tasksToManage: Task[]) => {
    const { createTask, updateTask, deleteTask } = useTasks();

    // 1. Filtragem e Visualização
    const { statusFilter, setStatusFilter, filteredTasks } = useTaskFiltering(tasksToManage);

    // 2. Ações Padronizadas
    const handleSaveTask = async (taskData: Partial<Task>, id?: string) => {
        try {
            if (id) {
                // Update: O Context espera o objeto Task completo
                const currentTask = tasksToManage.find(t => t.id === id);
                if (!currentTask) {
                    // Fallback se não achar na lista local (raro, mas possível em race conditions)
                    console.error("Tarefa não encontrada na lista local para update");
                    return false;
                }

                const updatedTask = { ...currentTask, ...taskData };
                await updateTask(updatedTask);
                notify.success('Tarefa atualizada com sucesso!');
            } else {
                // Create: O Context espera Omit<Task, 'id'>
                // Assumimos que o formulário valida os campos obrigatórios
                await createTask(taskData as Omit<Task, 'id'>);
                notify.success('Tarefa criada com sucesso!');
            }
            return true;
        } catch (error) {
            console.error(error);
            notify.error('Erro ao salvar tarefa.');
            return false;
        }
    };

    const handleDeleteTask = async (id: string) => {
        try {
            // O Context deleteTask espera string ID
            await deleteTask(id); // Retorna Promise<void>, então assumimos sucesso se não throw
            notify.success('Tarefa excluída com sucesso!');
            return true;
        } catch (error) {
            console.error(error);
            notify.error('Erro ao excluir tarefa.');
            return false;
        }
    };

    const handleToggleCompletion = async (task: Task) => {
        try {
            const newStatus = !task.isCompleted;
            // O Context updateTask espera objeto Task completo
            const updatedTask = { ...task, isCompleted: newStatus };
            await updateTask(updatedTask);

            notify.success(newStatus ? 'Tarefa concluída!' : 'Tarefa reaberta!');
            return true;
        } catch (error) {
            console.error(error);
            notify.error('Erro ao atualizar status da tarefa.');
            return false;
        }
    };

    const handleDuplicateTask = async (task: Task) => {
        try {
            const { id, ...taskData } = task;

            const newTask: Partial<Task> = {
                ...taskData,
                title: `${taskData.title} (Cópia)`,
                isCompleted: false,
                leadId: taskData.leadId || taskData.lead?.id,
            };

            await createTask(newTask as Omit<Task, 'id'>);
            notify.success('Tarefa duplicada com sucesso!');
            return true;
        } catch (error) {
            console.error(error);
            notify.error('Erro ao duplicar tarefa.');
            return false;
        }
    };

    return {
        tasks: filteredTasks,
        allTasks: tasksToManage,
        statusFilter,
        setStatusFilter,
        saveTask: handleSaveTask,
        deleteTask: handleDeleteTask,
        toggleTaskCompletion: handleToggleCompletion,
        duplicateTask: handleDuplicateTask
    };
};
